import {Component, OnInit, Optional, ChangeDetectionStrategy} from '@angular/core';

import {DialogService} from '../../../../../services/dialog/dialog.service';
import {OfferGoalFormComponent} from '../offer-goal-form/offer-goal-form.component';
import {GoalService} from '../../../../../services/goal/goal.service';
import {Goal} from '../../../../../models/goal';
import {OfferTabComponent} from '../offer-tab/offer-tab.component';
import {Offer} from '../../../../../models/offer';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {OfferGoalService} from '../../../../../services/offer-goal/offer-goal.service';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {OfferService} from '../../../../../services/offer/offer.service';
import {OfferGoal} from '../../../../../models/offer-goal';
import {FormsModule} from '@angular/forms';
import {CurrencyPipe} from '../../../../../pipes/currency/currency.pipe';
import {PercentPipe} from '../../../../../pipes/percent/percent.pipe';
import {IsGranted} from '../../../../../decorator/is-granted';
import {AuthService} from '../../../../../services/auth/auth.service';
import {IsGrantedDirective} from '../../../../../directives/is-granted/is-granted.directive';
import {MetaTitle} from '../../../../../decorator/meta-title';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-offer-goal-list',
  imports: [
    InputSwitchComponent,
    FormsModule,
    CurrencyPipe,
    PercentPipe,
    IsGrantedDirective
],
  templateUrl: './offer-goal-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './offer-goal-list.component.scss'
})
@IsGranted(['ROLE_API_OFFER_READ'])
@MetaTitle('Goal offers')
export class OfferGoalListComponent implements OnInit {
  loaded = false;
  offer: Offer | null = null;
  goals: Goal[] = [];
  goalInfoList: any[] = [];

  constructor(
    private dialog: DialogService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private auth: AuthService,
    private offerService: OfferService,
    private goalService: GoalService,
    private offerGoalService: OfferGoalService,
    @Optional() private offerTab: OfferTabComponent
  ) {
  }

  ngOnInit(): void {
    this.goalService.findAll({sort: 'name'}).subscribe(goals => {
      this.goals = goals;

      this.offerTab.offerSubject.subscribe(offer => {
        this.offer = offer;
        if (this.offer) {

          this.goalInfoList = [];
          this.goals.forEach((goal: Goal) => {
            this.goalInfoList.push({
              goal,
              offerGoal: this.findOfferGoal(goal),
              detailExpanded: false,
            });
          });

          this.loaded = true;
        }
      })
    });
  }

  onChangeStatus(goal: Goal, e: any): void {
    if (e.target.checked) {
      this.addGoal(goal);
    } else {
      this.deleteGoal(goal);
    }
  }

  findOfferGoal(goal: Goal): OfferGoal | null {
    const offerGoal = this.offer?.offerGoals.find((offerGoal: OfferGoal) => offerGoal.goal?.id === goal.id);

    return offerGoal ?? null;
  }

  addGoal(goal: Goal): void {
    this.loader.startLoading();
    this.offerGoalService.create({
      offerId: this.offer?.id,
      goalId: goal.id
    }).subscribe(() => {
      this.offerTab.refresh();
    });
  }

  deleteGoal(goal: Goal): void {
    const offerGoal = this.findOfferGoal(goal);

    if (!offerGoal?.id) {
      return;
    }

    if (this.isOpenToAffiliates() && this.offer?.offerGoals.length === 1) {
      this.snackbar.error('An offer open to affiliates requires at least one goal.');
      this.offerTab.refresh();
      return;
    }

    this.loader.startLoading();
    this.offerGoalService.delete(offerGoal.id).subscribe({
      next: () => this.offerTab.refresh(),
      error: () => this.offerTab.refresh(),
    });
  }

  isOpenToAffiliates(): boolean {
    return !this.offer?.targetAccountType || this.offer.targetAccountType === 'affiliate';
  }

  isMissingGoal(): boolean {
    return this.isOpenToAffiliates() && !this.offer?.offerGoals.length;
  }

  edit(offerGoal: OfferGoal): void {
    this.dialog.openDialog(OfferGoalFormComponent, {
      class: 'modal-lg',
      data: {offerGoal}
    }).subscribe(() => {
      this.offerTab.refresh();
    });
  }

  toggleDetails(goalInfo: any): void {
    goalInfo.detailExpanded = !goalInfo.detailExpanded;
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_OFFER_WRITE']);
  }
}
